import React, { useState } from 'react';
import { Upload, FileText, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { exportAndDownload } from '../utils/downloadPdf';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ImportExportPage: React.FC = () => {
  const { user } = useAuth();

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importDryRun, setImportDryRun] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string } | null>(null);

  const [exportingDir, setExportingDir] = useState(false);
  const [exportMsg, setExportMsg] = useState('');

  const canImport = user?.role === 'Administrator' || user?.role === 'Superuser';
  const canExport = user?.role === 'Administrator' || user?.role === 'Superuser';

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await importFile.text();
      let data: unknown[];
      try {
        data = JSON.parse(text);
      } catch {
        setImportResult({ success: false, message: 'Invalid JSON. Please upload a valid JSON file.' });
        setImporting(false);
        return;
      }
      const res = await api.importContacts({ records: data, dry_run: importDryRun });
      setImportResult({
        success: true,
        message: res.data?.message ?? `Processed ${Array.isArray(data) ? data.length : '?'} records.`,
      });
    } catch (err: any) {
      setImportResult({
        success: false,
        message: err?.response?.data?.error ?? 'Import failed.',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleExportDirectory = async () => {
    setExportingDir(true);
    setExportMsg('');
    try {
      await exportAndDownload(() => api.exportDirectoryPDF(), 'directory.pdf');
      setExportMsg('PDF downloaded successfully.');
    } catch (err: any) {
      setExportMsg(err?.message ?? 'Export failed. Try again.');
    } finally {
      setExportingDir(false);
    }
  };

  return (
    <div style={{ maxWidth: 700, paddingBottom: 40 }}>
      {/* Import section */}
      {canImport && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Import Contacts</h2>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--gray-500)' }}>
            Upload a JSON array of contact records. Run a dry-run first to preview changes before committing.
          </p>

          <div
            style={{
              border: '2px dashed var(--gray-300)',
              borderRadius: 8,
              padding: 24,
              textAlign: 'center',
              marginBottom: 16,
              background: importFile ? 'var(--green-50)' : 'transparent',
              cursor: 'pointer',
            }}
            onClick={() => document.getElementById('import-file-input')?.click()}
          >
            <Upload size={24} color="var(--gray-400)" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 14, color: 'var(--gray-600)' }}>
              {importFile ? (
                <>
                  <strong>{importFile.name}</strong>
                  <span style={{ color: 'var(--gray-500)', marginLeft: 8 }}>
                    ({(importFile.size / 1024).toFixed(1)} KB)
                  </span>
                </>
              ) : (
                'Click to choose a JSON file'
              )}
            </div>
            <input
              id="import-file-input"
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={(e) => {
                setImportFile(e.target.files?.[0] ?? null);
                setImportResult(null);
              }}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={importDryRun}
              onChange={(e) => setImportDryRun(e.target.checked)}
            />
            <span>
              <strong>Dry run</strong> — preview changes without saving
            </span>
          </label>

          <button
            className="btn btn-primary"
            disabled={!importFile || importing}
            onClick={handleImport}
          >
            <Upload size={14} />
            {importing ? 'Importing…' : importDryRun ? 'Preview Import' : 'Import'}
          </button>

          {importResult && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 14px',
                borderRadius: 6,
                background: importResult.success ? 'var(--green-50)' : 'var(--red-50)',
                color: importResult.success ? 'var(--green-700)' : 'var(--red-700)',
                fontSize: 14,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
              }}
            >
              {importResult.success ? <CheckCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />}
              {importResult.message}
            </div>
          )}
        </div>
      )}

      {/* Export section */}
      {canExport && (
        <div className="card">
          <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Export</h2>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--gray-500)' }}>
            Generate PDF exports. Contact and agency PDFs are available on their respective detail pages.
          </p>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div
              className="card"
              style={{
                padding: '16px 20px',
                flex: 1,
                minWidth: 200,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                cursor: 'pointer',
                border: '1px solid var(--gray-200)',
              }}
              onClick={handleExportDirectory}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={20} color="var(--blue)" />
                <strong style={{ fontSize: 14 }}>Full Directory PDF</strong>
              </div>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--gray-500)' }}>
                Generate a complete directory PDF of all active contacts.
              </p>
              <button
                className="btn btn-primary"
                style={{ alignSelf: 'flex-start', marginTop: 4 }}
                disabled={exportingDir}
                onClick={(e) => { e.stopPropagation(); handleExportDirectory(); }}
              >
                <Download size={13} />
                {exportingDir ? 'Generating…' : 'Generate PDF'}
              </button>
            </div>
          </div>

          {exportMsg && (
            <p style={{ fontSize: 14, color: 'var(--gray-600)', marginTop: 12, marginBottom: 0 }}>{exportMsg}</p>
          )}
        </div>
      )}

      {!canImport && !canExport && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--gray-500)', padding: 40 }}>
          You don't have permission to use import/export features.
        </div>
      )}
    </div>
  );
};

export default ImportExportPage;
